import {forwardRef} from 'react';
import classNames from 'classnames';
import {useIntl} from 'react-intl';

import {Avatar} from '@time-webkit/all/atoms/avatar';
import {TypographySize, useTypography} from '@time-webkit/all/hooks/typography';
import {ArchiveIcon} from '@time-webkit/all/icons/archive';
import {AvatarStatus} from '@time-webkit/all/atoms/avatar-status';

import {type UserSuggestionProps} from '../types';
import sharedStyles from '../styles.module.css';

import styles from './styles.module.css';

export const UserSuggestion = forwardRef<HTMLDivElement, UserSuggestionProps>((props, ref) => {
    const intl = useIntl();
    const [headerTypography, descriptionTypography, badgeTypography, unreadTypography] = useTypography([
        {size: TypographySize.BodyL},
        {size: TypographySize.BodyM},
        {size: TypographySize.BodyXSSTrue},
        {size: TypographySize.BodyXSTrue},
    ]);

    const position = props.userPosition ? `∙ ${props.userPosition}` : '';
    const department = props.userDepartment ? `∙ ${props.userDepartment}` : '';

    return (
        <div
            data-test-id='userItem'
            ref={ref}
            className={classNames(sharedStyles.suggestion_container, props.className)}
            onClick={props.onClick}
        >
            <div className={styles.avatar_container}>
                <Avatar
                    src={props.avatar}
                    username={props.username}
                    size={36}
                />
                {props.status && (
                    <AvatarStatus
                        className={styles.avatar_status}
                        status={props.status}
                        size={14}
                    />
                )}
            </div>

            <div className={sharedStyles.info_container}>
                <div className={classNames(sharedStyles.header_container, sharedStyles.description_text)}>
                    <span className={headerTypography}>{props.fullName}</span>
                    {props.customStatus}
                    {props.deactivated && (
                        <ArchiveIcon
                            width={16}
                            height={16}
                        />
                    )}
                    {props.badge && (
                        <div className={classNames(sharedStyles.badge, badgeTypography)}>{props.badge}</div>
                    )}
                </div>
                <div
                    className={classNames(
                        sharedStyles.description_container,
                        sharedStyles.description_text,
                        descriptionTypography,
                    )}
                >
                    <span className={classNames(sharedStyles.login_text, descriptionTypography)}>
                        @{props.username}
                    </span>{' '}
                    {props.deactivated ? (
                        <span>
                            ∙{' '}
                            {intl.formatMessage({
                                id: 'suggestion.user.deactivated',
                                defaultMessage: 'Deactivated',
                            })}
                        </span>
                    ) : (
                        <span>{`${position} ${department}`}</span>
                    )}
                </div>
            </div>
            {Boolean(props.unreadCount) && (
                <div className={sharedStyles.unread_container}>
                    <div className={classNames(sharedStyles.unread, unreadTypography)}>{props.unreadCount}</div>
                </div>
            )}
        </div>
    );
});
